import { FormSectionHandler } from '../entity.handler';
import { AdminEditorService } from '../../admin.service';
import { FormBuilderField } from 'app/shared/components/form-builder';
import { TextField, EntityUtils, NumberField, TimeField, ComboField, CheckListField } from '../utils';
import { ICONS } from '../icons';
import { ListFieldValue } from 'app/shared/components/form-builder/list/list.component';
import * as uuid from 'uuid';
import { GeoLockProfileResponse, GeoLockProfileUpdateRequest, GeoLockTimeItem } from '@key-telematics/fleet-api-client';

interface EditorValues {
    name?: string;
    radiusKm?: number;
    items?: ListFieldValue<GeoLockTimeItem>[];
}

const DAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

export class GeoLockProfileDetailsSectionHandler implements FormSectionHandler<GeoLockProfileResponse, EditorValues> {

    type = 'form';
    icon = ICONS.geolockprofile;
    bulk = false;

    title = this.translate('TITLE');
    description = this.translate('DESC');
    warning = null;

    constructor(
        public admin: AdminEditorService,
        public ownerId: string,
        private creating: boolean
    ) {
    }

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.GEOLOCKPROFILE.DETAILS.${key}`, values);
    }

    applyUpdate(entity: GeoLockProfileResponse, payload: GeoLockProfileUpdateRequest): Promise<GeoLockProfileResponse> {
        return this.admin.applyUpdate(api => api.entities.updateGeoLockProfile(entity.id, payload));
    }

    mapEntityToEditor(entity: GeoLockProfileResponse): Promise<EditorValues> {
        this.warning = entity.times && entity.times.length > 0 ? null : this.admin.translate(`EDITORS.GEOLOCKPROFILE.WARNINGS.ITEM_LENGTH`);
        let radiusKm = this.admin.units.fromBackend('distance', entity.radiusKm, 2);
        const maxRadius = this.admin.units.format(100, 'distance').value as number;
        if (radiusKm > Math.floor(maxRadius)) {
            radiusKm = Math.floor(maxRadius);
        }
        const result = {
            name: entity.name,
            radiusKm,
            items: entity.times?.map(x => this.stateItemToListItem(x)) || [],
        };
        Object.keys(result).forEach(key => result[key] === undefined && delete result[key]); // strip undefined properties from the object
        return Promise.resolve(result);
    }

    mapEditorToEntity(entity: GeoLockProfileUpdateRequest, values: EditorValues): Promise<GeoLockProfileUpdateRequest> {
        const patch: GeoLockProfileUpdateRequest = {
            name: values.name,
            radiusKm: this.admin.units.toBackend('distance', values.radiusKm) as number,
            times: values.items ? values.items.map(x => x.data) : undefined,
        };
        Object.keys(patch).forEach(key => patch[key] === undefined && delete patch[key]); // strip undefined properties from the object
        return Promise.resolve(EntityUtils.mergeFields(['name', 'radiusKm', 'times'], patch, entity));
    }

    getFields(entity: GeoLockProfileResponse): Promise<FormBuilderField[]> {
        return Promise.resolve().then(() => {
            return [
                TextField('name', this.translate('FIELDS.NAME'), true, 1, 255),
                NumberField('radiusKm', this.translate('FIELDS.RADIUS'), true, 0, Math.floor(this.admin.units.format(100, 'distance').value as number), this.admin.units.format(0, 'distance').unit),
                {
                    id: 'items', title: this.translate('FIELDS.ITEMS'), type: 'list', required: false, visible: !this.creating,
                    actions: [
                        { name: this.admin.i18n.instant('SHARED.ADD'), click: this.showAddTimeModal.bind(this), },
                        { id: 'edit', click: this.showAddTimeModal.bind(this), },
                    ],
                    getText: (field: FormBuilderField, values: any) => values[field.id] && values[field.id].map(x => `${x.name} - (${x.description})`).join('<br/>'),
                },
            ];
        }).then(fields => EntityUtils.prepareBulk(!entity, [], fields));
    }

    stateItemToListItem(item: GeoLockTimeItem): ListFieldValue<GeoLockTimeItem> {
        return {
            id: item.id,
            name: this.translate('FIELDS.FROM_TO', { from: item.from, to: item.to } ),
            icon: 'clock',
            description: item.days.sort().map(x => this.admin.i18n.instant(`SHARED.WEEK_DAYS.LONG.${DAYS[x]}`)).join(', '),
            data: item,
        };
    }

    updateOrAddItem<T extends { id: string }>(items: ListFieldValue<any>[], existingItem: ListFieldValue<any>, curfew: T, itemToListItem: (curfew: T) => ListFieldValue<any>) {
        if (existingItem) {
            const idx = items.findIndex(x => x.id === existingItem.id);
            items[idx] = itemToListItem(curfew);
        } else {
            items.push(itemToListItem(curfew));
        }
    }

    async showAddTimeModal(_action: string, _field: FormBuilderField, values: EditorValues, item: ListFieldValue<any>): Promise<GeoLockTimeItem> {

        const input = (item && item.data) || {
            id: uuid.v4(),
            from: '00:00:00',
            to: '23:59:00',
            timeZoneId: this.admin.user.timeZoneId,
            days: ['0', '1', '2', '3', '4', '5', '6'],
        };
        input.days = input.days.map(x => x.toString()); // make sure the days values are strings, as that is required for the check list field

        const timeZones = await this.admin.getTimeZones();
        return this.admin.showFormModal<GeoLockTimeItem>({
            groups: [{
                name: this.translate('MODALS.TIME.TITLE'),
                description: this.translate('MODALS.TIME.DESC'),
                fields: [
                    TimeField('from', this.translate('FIELDS.FROM'), true),
                    TimeField('to', this.translate('FIELDS.TO'), true),
                    ComboField('timeZoneId', this.translate('FIELDS.TIMEZONE'), true, timeZones.map(x => ({ key: x.id, value: x.name }))),
                    CheckListField('days', this.translate('FIELDS.DAYS'), true, DAYS.map((key, index) => ({ key: index.toString(), value: this.admin.i18n.instant(`SHARED.WEEK_DAYS.LONG.${key}`) }))),
                ],
            }],
        }, input).then(time => {
            if (time) {
                this.updateOrAddItem(values.items, item, time, this.stateItemToListItem.bind(this));
            }
            return time;
        });

    }

}
